/**
 * UK business-day arithmetic mirroring the backend's
 * `vnext/core/business_days.py` — the TAT ↔ due-date coupling in the
 * triage commercials box must agree exactly with the server's due-date
 * computation.
 *
 * Semantics (same as the backend):
 * - The count starts the day AFTER the anchor (a 1-day turnaround
 *   started Monday is due Tuesday; started Friday it's due Monday).
 * - Saturdays, Sundays and England bank holidays are skipped. The
 *   holiday calendar comes from `/meta/uk-bank-holidays` (see
 *   `useUkBankHolidays`) — an empty set degrades to weekend-only
 *   skipping.
 *
 * All arithmetic is done on UTC dates ("YYYY-MM-DD" anchors) so the
 * browser's local timezone can never shift a due date across midnight.
 */

const DAY_MS = 86_400_000;

/** "YYYY-MM-DD" for a Date's UTC calendar day. */
export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Parse "YYYY-MM-DD" to a UTC-midnight Date (null for invalid input). */
export function parseIsoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isBusinessDay(date: Date, holidays: ReadonlySet<string>): boolean {
  const dow = date.getUTCDay();
  return dow !== 0 && dow !== 6 && !holidays.has(toIsoDate(date));
}

/**
 * `days` UK business days after `start` (UTC-day granularity).
 * `days <= 0` returns the start's UTC day unchanged.
 */
export function addBusinessDays(start: Date, days: number, holidays: ReadonlySet<string>): Date {
  let current = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  let remaining = Math.trunc(days);
  while (remaining > 0) {
    current = new Date(current.getTime() + DAY_MS);
    if (isBusinessDay(current, holidays)) {
      remaining -= 1;
    }
  }
  return current;
}

/**
 * A due date for display: "21 Aug 2026".
 *
 * Due dates are a UTC calendar day, not an instant — the backend stores
 * them as 23:59:59Z on the due day, and date-only fields (NP due dates)
 * arrive as bare "YYYY-MM-DD". Formatting either in the browser's local
 * zone rolls the day over for any viewer east of UTC — in British Summer
 * Time 23:59:59Z is 00:59 the *next* morning, so every due date read a
 * day late. Always render the UTC day.
 */
export function formatDueDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A due value as a comparable instant (ms).
 *
 * Date-only dues — how partner deadlines arrive, from a DB `Date`
 * column — mean the END of that day, the same 23:59:59Z convention the
 * backend stamps on report dues. Parsing "2026-08-21" straight through
 * gives UTC midnight, which reads as overdue a full day early.
 */
export function dueInstant(value: string): number {
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T23:59:59Z` : value;
  return new Date(iso).getTime();
}

/**
 * Business days in the interval `(start, end]` — the inverse of
 * `addBusinessDays`: for a due date D, the TAT such that
 * `addBusinessDays(start, TAT)` lands on (or just before) D. Returns 0
 * when `end` is on or before `start`'s UTC day.
 */
export function businessDaysBetween(start: Date, end: Date, holidays: ReadonlySet<string>): number {
  let current = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const target = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  let count = 0;
  while (current.getTime() < target) {
    current = new Date(current.getTime() + DAY_MS);
    if (isBusinessDay(current, holidays)) {
      count += 1;
    }
  }
  return count;
}
